import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../baseQuery';
import { MTGCard } from 'types/interfaces/mtgCard';

interface CardsSearchRequest {
  name?: string;
  exact?: boolean;
  oracleText?: string;
  typeLine?: string;
  setName?: string;
  exactSetName?: boolean;
  colors?: string[];
  manaCost?: string;
  cmc?: number;
  cmc_comparison?: 'eq' | 'gt' | 'lt';
  power?: string;
  power_comparison?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  toughness?: string;
  toughness_comparison?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  artist?: string;
  flavorText?: string;
  lang?: string;
  format?: string;
  formatLegality?: 'legal' | 'not_legal';
  limit?: number;
  filter?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
}

export interface CardsSearchResponse {
  cards: MTGCard[];
  page: number;
  hasMore: boolean;
  totalCount: number;
}

export interface ConsolidatedCard {
  cardName: string;
  oracleId: string;
  totalVersions: number;
  sampleImage: {
    normal?: string;
    small?: string;
    art_crop?: string;
  };
  lowestPrice?: number;
  setNames?: string[];
}

export interface ConsolidatedSearchResponse {
  cards: ConsolidatedCard[];
  totalResults: number;
  hasMore: boolean;
  page: number;
}

// Backend response for /cards/:id and /cards/by-printing is FLAT: MTGCard fields
// at the top level, with `printings` as a sibling array. There is no `.card` wrapper.
export interface CardDetailResponse extends MTGCard {
  printings?: Array<{
    id: number;
    name: string;
    setName: string;
    collectorNumber: string;
    foil: boolean;
    nonFoil: boolean;
    imageUris: any;
    points?: number;
    rarity: string;
    releasedAt: Date;
  }>;
  // Backend may also include lowercase `scryfallId` alongside the canonical `scryfallID`.
  scryfallId?: string;
}

const buildSearchParams = (params: CardsSearchRequest & { page: number }): string => {
  const searchParams = new URLSearchParams();
  
  // Add each parameter if it exists
  if (params.name) searchParams.append('name', params.name);
  if (params.exact !== undefined) searchParams.append('exact', params.exact.toString());
  if (params.oracleText) searchParams.append('oracle_text', params.oracleText);
  if (params.typeLine) searchParams.append('type_line', params.typeLine);  
  if (params.setName) searchParams.append('set_name', params.setName);
  if (params.exactSetName !== undefined) searchParams.append('exact_set_name', params.exactSetName.toString());
  if (params.colors && params.colors.length > 0) {
    searchParams.append('colors', params.colors.join(','));
  }
  if (params.manaCost) searchParams.append('mana_cost', params.manaCost);
  if (params.cmc !== undefined) searchParams.append('cmc', params.cmc.toString());
  if (params.cmc_comparison) searchParams.append('cmc_comparison', params.cmc_comparison);
  if (params.power) searchParams.append('power', params.power);
  if (params.power_comparison) searchParams.append('power_comparison', params.power_comparison);
  if (params.toughness) searchParams.append('toughness', params.toughness);
  if (params.toughness_comparison) searchParams.append('toughness_comparison', params.toughness_comparison);
  if (params.artist) searchParams.append('artist', params.artist);
  if (params.flavorText) searchParams.append('flavor_text', params.flavorText);
  if (params.lang) searchParams.append('lang', params.lang);
  if (params.format) searchParams.append('format', params.format);
  if (params.formatLegality) searchParams.append('formatLegality', params.formatLegality);
  if (params.filter) searchParams.append('filter', params.filter);
  if (params.sort) searchParams.append('sort', params.sort);
  if (params.direction) searchParams.append('direction', params.direction);

  // Always add page and limit (increased default for better UX)
  searchParams.append('page', params.page.toString());
  searchParams.append('limit', (params.limit || 40).toString());
  
  return searchParams.toString();
};

interface EstimatePriceRequest {
  cardId: string;
  language: string;
  condition: string;
  foil: boolean;
}

interface EstimatePriceData {
  price: number;
  languageMultiplierApplied: boolean;
  multiplierValue: number;
}

export const cardsSearchApi = createApi({
  reducerPath: 'cardsSearchApi',
  tagTypes: ['CardsSearch'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    cardsSearch: builder.infiniteQuery<
      CardsSearchResponse,
      CardsSearchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => ({
        url: `/cards/search-light?${buildSearchParams({ ...queryArg, page: pageParam })}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: (_, __, arg) => [
        { type: 'CardsSearch', id: JSON.stringify(arg) }, // scoped to the search params
      ],
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
    }),
    // Get single card by ID
    getCardById: builder.query<CardDetailResponse, string | number>({
      query: (cardId) => {
        console.log('🔍 Fetching card with ID:', cardId);
        return {
          url: `/cards/${cardId}`,
          method: 'GET',
          requiresAuth: false, // Public endpoint - no authentication required for SEO
        };
      },
      providesTags: (_, __, id) => [{ type: 'CardsSearch', id: `card-${id}` }],
    }),
    // Get all printings of a card
    getCardPrintings: builder.query<MTGCard[], { cardId: string | number; languages?: string[] }>({
      query: ({ cardId, languages }) => ({
        url: `/cards/${cardId}/printings${languages?.length ? `?languages=${languages.join(',')}` : ''}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: (_, __, { cardId, languages }) => [
        { type: 'CardsSearch', id: `printings-${cardId}-${languages?.join(',') || 'default'}` },
      ],
    }),
    // Regular search endpoint for single page results
    searchCards: builder.query<CardsSearchResponse, CardsSearchRequest & { page?: number }>({
      query: (params) => ({
        url: `/cards/search-light?${buildSearchParams({ ...params, page: params.page || 1 })}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: ['CardsSearch'],
    }),
    // Consolidated search endpoint for deduplicated results
    searchConsolidated: builder.infiniteQuery<
      ConsolidatedSearchResponse,
      CardsSearchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => ({
        url: `/cards/search-consolidated?${buildSearchParams({ ...queryArg, page: pageParam })}`,
        method: 'GET',
        requiresAuth: false, // Public endpoint - no authentication required for SEO
      }),
      providesTags: (_, __, arg) => [
        { type: 'CardsSearch', id: `consolidated-${JSON.stringify(arg)}` },
      ],
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
    }),
    // Get single card by set code + collector number (slug URL lookup)
    getCardByPrinting: builder.query<CardDetailResponse, { setCode: string; collectorNumber: string }>({
      query: ({ setCode, collectorNumber }) => ({
        url: `/cards/by-printing?set=${encodeURIComponent(setCode)}&number=${encodeURIComponent(collectorNumber)}`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (_, __, { setCode, collectorNumber }) => [
        { type: 'CardsSearch', id: `printing-${setCode}-${collectorNumber}` },
      ],
    }),
    // Estimate price for a single card with language multiplier info
    estimatePrice: builder.mutation<EstimatePriceData, EstimatePriceRequest>({
      query: (data) => ({
        url: '/cards/estimate-price',
        method: 'POST',
        body: data,
        requiresAuth: true,
      }),
      transformResponse: (response: { success: boolean; data: EstimatePriceData }) => response.data,
    }),
  }),
});

export const {
  useCardsSearchInfiniteQuery,
  useSearchCardsQuery,
  useGetCardByIdQuery,
  useLazyGetCardByIdQuery,
  useGetCardByPrintingQuery,
  useGetCardPrintingsQuery,
  useSearchConsolidatedInfiniteQuery,
  useEstimatePriceMutation,
} = cardsSearchApi;
